/** Share helpers for wedding invite code + link. */
import vkBridge from '@vkontakte/vk-bridge';

export interface InviteShare {
  code: string;
  link: string;
  title?: string;
}

export function buildInviteText({ code, link, title }: InviteShare): string {
  const name = title ? `«${title}»` : 'свадьбы';
  return `Приглашение в свадьбу ${name}\nКод: ${code}\nОткройте Colibri → «Вступить по коду» → введите код.\nИли по ссылке: ${link}`;
}

export function telegramShareUrl(text: string): string {
  return `https://t.me/share/url?url=${encodeURIComponent(text)}`;
}

export function whatsappShareUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export function vkShareUrl(text: string, link: string): string {
  // m.vk.com works better inside mobile VK webview
  return `https://m.vk.com/share.php?url=${encodeURIComponent(link)}&title=${encodeURIComponent(text)}`;
}

/** Share to VK: native Mini App bridge first, then web dialog. */
export async function shareToVK(text: string, link: string): Promise<boolean> {
  try {
    await vkBridge.send('VKWebAppShare', { link });
    return true;
  } catch {
    return openExternalShare(vkShareUrl(text, link));
  }
}

export function viberShareUrl(text: string): string {
  return `viber://forward?text=${encodeURIComponent(text)}`;
}

/** MAX has no public web share endpoint — open app/web after copy. */
export function maxShareUrl(_text: string): string {
  return `https://max.ru/`;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }
}

export function canWebShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/** Try native share sheet. Returns true if the OS sheet was opened. */
export async function tryNativeShare(invite: InviteShare): Promise<boolean> {
  if (!canWebShare()) return false;
  const text = buildInviteText(invite);
  try {
    await navigator.share({
      title: invite.title ? `Свадьба «${invite.title}»` : 'Colibri — приглашение',
      text,
      url: invite.link,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Open external / custom-scheme URL from a VK Mini App webview.
 * window.open is often blocked there — try several strategies.
 */
export function openExternalShare(url: string): boolean {
  // 1) same-tab assign — works in VK webview where window.open is blocked
  try {
    window.location.assign(url);
    return true;
  } catch {
    /* fall through */
  }

  // 2) hidden anchor click
  try {
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener noreferrer';
    a.target = '_blank';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return true;
  } catch {
    /* fall through */
  }

  // 3) window.open
  try {
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    return !!w;
  } catch {
    return false;
  }
}

export type ShareTarget =
  | { kind: 'copy' }
  | { kind: 'url'; url: string }
  | { kind: 'max' };
